/* eslint-disable react/prop-types */
import React, { useState, useEffect } from 'react';
import {
  Form,
  Col,
  Spin,
  Input,
  DatePicker,
  Table,
  Pagination,
  Row,
  Card,
} from 'antd';
import {
  getClasificacionesDeComercioCatalogo,
  getTiposDeEmpresas,
  getTiposDeEstablecimiento,
  getUnidadesDeRecaudacion,
  getClasesDeActividadesEmpresariales,
} from 'api/empresas';
import {
  getEmpresas,
  postEmpresa,
  patchEmpresa,
} from 'api/empresas/empresas';
import Select from 'components/Select';
import { DATE_FRONTEND_FORMAT, DATE_FORMAT } from 'utils/formatValues';
import LadaNumero from 'components/LadaNumero';
import FormSubmitCustomControls from 'components/FormSubmitCustomControls';
import {
  EditOutlined, EnvironmentFilled, EyeOutlined, PlusOutlined,
} from '@ant-design/icons';
import { toMoment } from 'utils/normalizers';
import Direccion, { formatAddressValues } from 'components/Direccion';
import address from 'utils/address';

const EmpresasDeContribuyente = ({
  contribuyente,
  mutable = true,
  fetchCallback,
  callback,
}) => {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState({ results: [], page: 1, count: 0 });
  const [establecimientos, setEstablecimientos] = useState([]);
  const [visible, setVisible] = useState(false);
  const [ubicacionesEmpresa, setUbicacionEmpresa] = useState([]);
  const [unidadesDeRecaudacion, setUnidadesDeRecaudacion] = useState([]);
  const [tiposDeEmpresa, setTiposDeEmpresa] = useState([]);
  const [claseActividad, setClaseActividad] = useState([]);
  const [clasificacionesDeComercio, setClasificacionesDeComercio] = useState([]);
  const [direccionFiscal, setDireccionFiscal] = useState();
  const [direccionNotificacion, setDireccionNotificacion] = useState();
  const [selectedRowKeys, setSelectedRowKeys] = useState([]);
  const selected = data.results.find((e) => e.id === selectedRowKeys[0]);

  const fetchData = async (page = 1) => {
    setLoading(true);
    const _data = await getEmpresas({ page, contribuyente: contribuyente.id });
    _data.page = page;
    setData(_data);
    if (fetchCallback) fetchCallback(_data);
    setLoading(false);
  };

  useEffect(() => {
    let mounted = true;
    const fetchAll = async () => {
      setLoading(true);
      const [
        _data,
        _establecimientos,
        _tiposEmpresa,
        _claseActividad,
        _clasificacionesDeComercio,
        _unidadesDeRecaudacion,
      ] = await Promise.all([
        getEmpresas({ page: 1, contribuyente: contribuyente.id }),
        getTiposDeEstablecimiento(),
        getTiposDeEmpresas(),
        getClasesDeActividadesEmpresariales(),
        getClasificacionesDeComercioCatalogo(),
        getUnidadesDeRecaudacion(),
      ]);
      // OPCIONES DE UBICACION DE EMPRESA
      const options = [{ id: null, nombre: 'NINGUNA' }];
      if (contribuyente.direcciones) {
        options.push({
          id: 1,
          nombre: 'COPIAR DIRECCIÓN FISCAL DEL CONTRIBUYENTE',
        });
      }
      if (contribuyente.direcciones_de_notificacion) {
        options.push({
          id: 2,
          nombre: 'COPIAR DIRECCIÓN DE NOTIFICACIÓN DEL CONTRIBUYENTE',
        });
      }
      options.push({
        id: 3,
        nombre: 'AGREGAR NUEVA DIRECCIÓN (SE CAPTURA DESPUÉS DE GUARDAR EL ESTABLECIMIENTO)',
      });
      if (mounted) {
        _data.page = 1;
        if (fetchCallback) fetchCallback(_data);
        setUbicacionEmpresa(options);
        setDireccionFiscal(contribuyente.direcciones);
        setDireccionNotificacion(contribuyente.direcciones_de_notificacion);
        setEstablecimientos(_establecimientos);
        setUnidadesDeRecaudacion(_unidadesDeRecaudacion);
        setClasificacionesDeComercio(_clasificacionesDeComercio);
        setClaseActividad(_claseActividad);
        setTiposDeEmpresa(_tiposEmpresa);
        setData(_data);
        setLoading(false);
      }
    };
    fetchAll();
    return () => { mounted = false; };
  }, []);

  const onCancel = () => {
    form.resetFields();
    setVisible(false);
    setSelectedRowKeys([]);
  };

  const onFinish = async () => {
    setLoading(true);
    const { direccion_de_ubicacion, ...values } = form.getFieldsValue();
    if ([1, 2].includes(direccion_de_ubicacion)) {
      const addressMap = {
        1: direccionFiscal,
        2: direccionNotificacion,
      };
      const direccion = addressMap[direccion_de_ubicacion];
      delete direccion.id;
      values.direccion_de_ubicacion = formatAddressValues(direccion);
      values.direccion_de_ubicacion.codigo_postal = direccion.codigo_postal.id;
    }
    values.contribuyente = contribuyente.id;
    const [key] = selectedRowKeys;
    const saved = await (key ? patchEmpresa(key, values) : postEmpresa(values));
    if (saved) {
      if (callback) {
        saved.message = `Establecimiento ${saved.clave} ${key ? 'agregada' : 'modificada'}`;
        callback(saved);
      }
      onCancel();
      if (direccion_de_ubicacion === 3) {
        const page = data.count % 10 === 0
          ? (data.count / 10) || 1
          : (parseInt(data.count / 10, 10) + 1);
        await fetchData(page);
        setTimeout(() => {
          setSelectedRowKeys([saved.id]);
          setVisible('address');
        });
      } else {
        await fetchData();
      }
    }
    setLoading(false);
  };

  const onClickAdd = () => {
    onCancel();
    setVisible('form');
  };
  const onClickEdit = () => setVisible('form');
  const onClickAddress = () => setVisible('address');

  const columns = [
    {
      title: 'Clave del Establecimiento',
      key: 'clave',
      dataIndex: 'clave',
      width: 200,
    },
    {
      title: 'Nombre del Establecimiento',
      key: 'nombre_comercial',
      dataIndex: 'nombre_comercial',
      width: 300,
    },
    fetchCallback && {
      title: 'Tipo de Establecimiento',
      key: 'tipo_de_establecimiento',
      dataIndex: 'tipo_de_establecimiento',
      render: (est) => establecimientos.find((e) => e.id === est)?.nombre,
      width: 300,
    },
    fetchCallback && {
      title: 'Dirección',
      key: 'direccion_de_ubicacion',
      dataIndex: 'direccion_de_ubicacion',
      render: address,
      width: 300,
    },
    {
      title: 'Fecha de Apertura',
      key: 'fecha_de_apertura',
      dataIndex: 'fecha_de_apertura',
      render: (text) => toMoment(text, DATE_FORMAT)?.format(DATE_FORMAT),
      width: 150,
    },
  ].filter((e) => e);

  const onRow = (record) => ({
    onClick: () => {
      setSelectedRowKeys([record.id]);
      form.setFieldsValue(record);
    },
    onDoubleClick: () => {
      setSelectedRowKeys([record.id]);
      form.setFieldsValue(record);
      onClickEdit();
    },
  });

  const rowSelection = {
    selectedRowKeys,
    type: 'radio',
  };

  return (
    <Col span={24}>
      <Spin tip="Cargando..." spinning={loading}>
        {visible !== 'form' && (
          <>
            <FormSubmitCustomControls
              controls={[
                mutable && {
                  text: 'Agregar',
                  icon: <PlusOutlined />,
                  onClick: () => onClickAdd(),
                  disabled: !mutable,
                },
                {
                  text: mutable ? 'Editar' : 'Ver',
                  icon: mutable ? <EditOutlined /> : <EyeOutlined />,
                  onClick: () => onClickEdit(),
                  disabled: !selectedRowKeys.length,
                },
                {
                  text: 'Dirección',
                  icon: <EnvironmentFilled />,
                  onClick: () => onClickAddress(),
                  disabled: !selectedRowKeys.length,
                },
              ].filter((e) => e)}
              style={{ width: '100%' }}
              justify="end"
            />
            <Table
              columns={columns}
              dataSource={data.results}
              rowKey="id"
              pagination={false}
              rowSelection={rowSelection}
              onRow={onRow}
              scroll={{
                y: 600,
                x: columns.reduce((acum, curr) => acum + curr.width, 0),
                scrollToFirstRowOnChange: true,
              }}
            />
            <Pagination
              current={data.page}
              onChange={(p) => fetchData(p)}
              total={data.count}
            />
          </>
        )}
        {visible === 'form' && (
          <Card
            title={`${selectedRowKeys.length ? '' : 'Agregar '}Establecimiento`}
            extra={(
              <FormSubmitCustomControls
                onFinish={mutable ? form.submit : null}
                onCancel={onCancel}
                loading={loading}
              />
            )}
          >
            <Form
              name="info"
              layout="vertical"
              form={form}
              onFinish={() => onFinish()}
              initialValues={{
                direccion_de_ubicacion: null,
                unidad_recaudadora: contribuyente.unidad_de_recaudacion,
              }}
              scrollToFirstError
            >
              <Row gutter={10}>
                {!!selectedRowKeys.length && (
                  <Col md={8} sm={24} xs={24}>
                    <Form.Item
                      label="Clave del Establecimiento"
                      name="clave"
                    >
                      <Input disabled />
                    </Form.Item>
                  </Col>
                )}
                <Col md={8} sm={24} xs={24}>
                  <Form.Item
                    label="Nombre del Establecimiento"
                    name="nombre_comercial"
                    rules={[{ required: true }]}
                  >
                    <Input disabled={!mutable} allowClear />
                  </Form.Item>
                </Col>
                <Col xs={24} sm={24} md={8}>
                  <Form.Item
                    name="unidad_recaudadora"
                    label="Unidad Recaudadora"
                    tooltip="Seleccione la Unidad de Recaudación más cercana a su domicilio"
                  >
                    <Select
                      disabled={!mutable}
                      options={unidadesDeRecaudacion}
                      allowClear
                    />
                  </Form.Item>
                </Col>
                <Col xs={24} sm={24} md={8}>
                  <Form.Item
                    name="tipo_de_empresa"
                    label="Tipo de Empresa"
                    hasFeedback
                  >
                    <Select disabled={!mutable} options={tiposDeEmpresa} />
                  </Form.Item>
                </Col>
                <Col md={8} sm={24} xs={24}>
                  <Form.Item
                    label="Tipo de establecimiento"
                    name="tipo_de_establecimiento"
                  >
                    <Select
                      disabled={!mutable}
                      options={establecimientos}
                      getLabel={(e) => e.nombre}
                    />
                  </Form.Item>
                </Col>
                <Col xs={24} sm={24} md={8}>
                  <Form.Item
                    name="clasificacion_de_comercio"
                    label="Clasificación de comercio"
                    hasFeedback
                  >
                    <Select
                      disabled={!mutable}
                      options={clasificacionesDeComercio}
                    />
                  </Form.Item>
                </Col>
                <Col xs={24} sm={24} md={8}>
                  <Form.Item
                    name="clase_actividad_empresarial_principal"
                    label="Clase Actividad"
                    hasFeedback
                    tooltip="Clase Actividad Empresarial"
                  >
                    <Select
                      disabled={!mutable}
                      options={claseActividad}
                      allowClear
                    />
                  </Form.Item>
                </Col>
                <Col xs={24} sm={24} md={8}>
                  <Form.Item
                    name="fecha_de_apertura"
                    label="Fecha de apertura"
                    hasFeedback
                  >
                    <DatePicker disabled={!mutable} format={DATE_FRONTEND_FORMAT} placeholder="" />
                  </Form.Item>
                </Col>
                {!!selectedRowKeys.length && (
                  <Col xs={24} sm={24} md={8}>
                    <Form.Item
                      name="fecha_de_cierre"
                      label="Fecha de cierre"
                      hasFeedback
                    >
                      <DatePicker disabled={!mutable} format={DATE_FRONTEND_FORMAT} placeholder="" />
                    </Form.Item>
                  </Col>
                )}
                <Col xs={24} sm={24} md={8}>
                  <Form.Item
                    name="fecha_de_alta_de_padron_empresa"
                    label="Fecha de alta de Padrón"
                    hasFeedback
                  >
                    <DatePicker disabled={!mutable} format="DD/MM/YYYY" placeholder="" />
                  </Form.Item>
                </Col>
                {!selectedRowKeys.length && (
                  <Col md={8} sm={24} xs={24}>
                    <Form.Item label="Dirección de Ubicación" name="direccion_de_ubicacion">
                      <Select
                        disabled={!mutable}
                        options={ubicacionesEmpresa}
                        getLabel={(e) => e.nombre}
                      />
                    </Form.Item>
                  </Col>
                )}
                <Col md={16} sm={24} xs={24}>
                  <LadaNumero
                    form={form}
                    names={{
                      lada: 'lada_fijo',
                      numero_de_telefono: 'telefono_fijo',
                    }}
                    tooltip="Teléfono Fijo del Establecimiento"
                    label="Teléfono Fijo"
                    fullSize
                    disabled={!mutable}
                  />
                </Col>
                <Col md={16} sm={24} xs={24}>
                  <LadaNumero
                    form={form}
                    names={{
                      lada: 'lada_celular',
                      numero_de_telefono: 'telefono_celular',
                    }}
                    tooltip="Teléfono Celular de la Establecimiento"
                    label="Teléfono Celular"
                    fullSize
                    disabled={!mutable}
                  />
                </Col>
                <Col xs={24} sm={24} md={16}>
                  <Form.Item name="observaciones" label="Observaciones">
                    <Input.TextArea
                      disabled={!mutable}
                      autoSize={{ minRows: 2, maxRows: 3 }}
                      maxLength={250}
                      allowClear
                    />
                  </Form.Item>
                </Col>
              </Row>
            </Form>
          </Card>
        )}
      </Spin>
      <Direccion
        modal
        isEditing={mutable}
        propName="direccion_de_ubicacion"
        setLoading={setLoading}
        loading={loading}
        fatherObject={visible ? selected : null}
        visible={visible === 'address'}
        onCancel={() => setVisible(false)}
        patchParent={patchEmpresa}
        callback={() => {
          setVisible(false);
          fetchData(data.page);
        }}
      />
    </Col>
  );
};

export default EmpresasDeContribuyente;
